import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { QRCodeSVG } from "qrcode.react";
import { site, sources } from "./content";
import { captureGrowthEvent, takeQuizStartProperties } from "./analytics";
import { trackMetaEvent } from "./meta";
import { useExperiment } from "./use-experiment";
import { isVariantOf, type Variant } from "./experiments";
import { Link, Arrow } from "@/components/landing/link";
import { Nav } from "@/components/landing/nav";
import { Footer } from "@/components/landing/footer";
import { Hero } from "@/components/landing/hero";
import { Showcase } from "@/components/landing/showcase";
import { Quote } from "@/components/landing/quote";
import { FAQs } from "@/components/landing/faqs";
import { JsonLd } from "@/components/landing/json-ld";
import { BackgroundBlur } from "@/components/landing/background-blur";

const Quiz = lazy(() => import("./quiz").then((module) => ({ default: module.Quiz })));
const CurrentPaywall = lazy(() =>
  import("./paywall").then((module) => ({ default: module.CurrentPaywall })),
);

/**
 * The funnel board is a working tool, not a page of the product: it is in every
 * dev build, and in a deployed one only when the build asked for it. Reading the
 * flag at module scope is what lets the bundler drop the chunk entirely from a
 * production build that did not.
 */
const devBoardEnabled = import.meta.env.DEV || import.meta.env.VITE_DEV_BOARD === "1";
const DevBoard = devBoardEnabled
  ? lazy(() => import("./dev-board").then((module) => ({ default: module.DevBoard })))
  : undefined;

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: site.name,
  url: site.url,
};

const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: site.name,
  url: site.url,
  description:
    "A calm, private GLP-1 treatment companion for tracking what happened and preparing for appointments.",
};

/**
 * Exported for the /dev board, which needs to draw both arms side by side. A
 * forced arm neither reads the flag nor reports a view — it is a picture of the
 * page, not a visit to it.
 */
export function Home({ variant: forced }: { variant?: Variant<"landing-message"> } = {}) {
  const live = useExperiment("landing-message", { track: forced === undefined });
  const variant = forced ?? live.variant;
  const resolved = forced !== undefined || live.resolved;
  const viewed = useRef(false);

  useEffect(() => {
    if (forced !== undefined || !resolved || viewed.current) return;
    viewed.current = true;
    captureGrowthEvent("landing_viewed", { experiment: "landing-message", variant });
  }, [forced, resolved, variant]);

  return (
    <main>
      <JsonLd data={[websiteSchema, organizationSchema]} />
      <Hero variant={variant} resolved={resolved} />
      <Showcase />
      <Quote />
      <FAQs variant={variant} />
    </main>
  );
}

const supportSections = [
  [
    "Using the app",
    "Vessa is a personal journal and reminder. It can help you log treatment events, check-ins, symptoms, measurements, and supply details. It does not provide medical advice, recommend doses, or make treatment decisions.",
  ],
  [
    "Medication questions",
    "For a missed dose, side effect, storage concern, or any question about your treatment, use the official medication guide and contact your prescriber or pharmacist. The app cannot tell you whether or when to take a dose.",
  ],
  [
    "Your information",
    "App-owned records stay on your device unless you choose to export them through the system share sheet. Delete-all removes app-owned records; HealthKit data is managed separately in Apple Health.",
  ],
  [
    "Contact",
    `For product support, email ${site.supportEmail}. Do not send medical details, medication instructions, photos of prescriptions, or urgent concerns by email.`,
  ],
];

function Page({ title, intro, children }: { title: string; intro: string; children: ReactNode }) {
  return (
    <main className="legal shell">
      <p className="kicker">{site.name}</p>
      <h1>{title}</h1>
      <p className="legal-intro">{intro}</p>
      <p className="updated">Last updated {site.lastUpdated}</p>
      <div className="prose">{children}</div>
    </main>
  );
}

function Support() {
  return (
    <Page
      title="Support, without the runaround."
      intro="The right next step for a product question, and the safer place to take a treatment question."
    >
      <div className="support-grid">
        {supportSections.map(([heading, body]) => (
          <section key={heading}>
            <h2>{heading}</h2>
            <p>{body}</p>
          </section>
        ))}
      </div>
      <aside className="notice">
        <strong>For urgent medical symptoms</strong>
        <p>Seek urgent or emergency care as appropriate. Do not wait for an app response.</p>
      </aside>
    </Page>
  );
}

function Privacy() {
  return (
    <Page
      title="Privacy that is easy to understand."
      intro="The product is designed so your app-owned treatment history stays on your device."
    >
      <h2>What the app stores</h2>
      <p>
        The iPhone app may store the health and treatment information you choose to record, such as
        medication plans, dose events, check-ins, symptoms, measurements, and supply details. This
        information is stored locally on your device.
      </p>
      <h2>Web quiz answers</h2>
      <p>
        Answers in the optional web quiz are used only in the open browser tab to render the next
        step and preview. They are not saved, added to a URL, submitted to Vessa, or sent to
        advertising or analytics services. Refreshing or closing the page discards them.
      </p>
      <h2>Name and email</h2>
      <p>
        If you ask us to hold your plan, we save the name and email you submit with Stripe so we
        can prepare checkout, associate a later purchase, and measure a generic lead. These fields
        are kept separate from quiz answers. They are not an agreement to receive promotional
        email.
      </p>
      <h2>Session-scoped website analytics</h2>
      <p>
        We use PostHog to measure a small, closed set of marketing events such as landing views,
        quiz starts, coarse progress, completion, and paywall views. PostHog assigns an ephemeral
        identifier in memory for the open page session. Automatic capture, session replay, form
        capture, persistent browser storage, and person profiles are disabled. Question identifiers,
        answer values, medication details, symptoms, body measurements, dates of birth, sex, and
        profiles derived from them are prohibited from analytics payloads.
      </p>
      <h2>Website requests</h2>
      <p>
        This static site is delivered through Cloudflare Pages. Like other web hosts, Cloudflare
        processes request information needed to deliver and protect the site, such as IP address and
        basic request metadata, under its own privacy terms. We do not send health logs or quiz
        answers to Cloudflare, PostHog, Meta, or AppsFlyer.
      </p>
      <h2>Optional Meta advertising measurement</h2>
      <p>
        If you choose “Allow Meta measurement,” Meta Pixel and the Conversions API receive only
        generic actions such as a page view, quiz start or completion, lead, paywall view, checkout
        start, and purchase. For matching, Meta may receive a hashed email for lead, checkout, and
        purchase events, along with browser identifiers and delivery metadata such as IP address and
        user agent. URLs are stripped of query parameters. Automatic form and button capture is
        disabled. Quiz answers, question names, medications, treatment details, symptoms,
        measurements, dates of birth, sex, and answer-derived segments are prohibited. You can
        reject or change this choice at any time using “Privacy choices.”
      </p>
      <h2>HealthKit</h2>
      <p>
        If you choose to connect Apple Health, permissions are requested for individual types at the
        time you use a feature. Apple Health controls its own data and permissions. An empty value
        in the app does not mean a permission was denied or that no data exists.
      </p>
      <h2>Exports and deletion</h2>
      <p>
        Exports happen through the iOS system share sheet when you choose to create one. Treat
        exported reports as sensitive. You can delete app-owned data in the app; deletion does not
        delete separate records held in Apple Health.
      </p>
      <h2>Changes</h2>
      <p>We will update this page when our practices change. Material changes will be dated here.</p>
    </Page>
  );
}

function MedicationSources() {
  return (
    <Page
      title="Official medication sources."
      intro="Medication-specific guidance depends on the exact product, presentation, and current label. These links are a starting point, not a substitute for your prescriber or pharmacist."
    >
      <div className="source-list">
        {sources.map((source) => (
          <article key={source.name}>
            <div>
              <h2>{source.name}</h2>
              <p>{source.detail}</p>
            </div>
            <a href={source.url} target="_blank" rel="noreferrer">
              Open source <Arrow />
            </a>
          </article>
        ))}
      </div>
      <aside className="notice">
        <strong>A note about missed doses and storage</strong>
        <p>
          Never rely on a generic weekly schedule. Use the current official label for your exact
          product and presentation, then ask a pharmacist or prescriber when you need individual
          guidance.
        </p>
      </aside>
    </Page>
  );
}

type HandoffState =
  | { status: "loading" }
  | { status: "ready"; oneLinkUrl: string }
  | { status: "error"; message: string };

function Welcome() {
  const [handoff, setHandoff] = useState<HandoffState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) {
      setHandoff({
        status: "error",
        message: "Open the link Stripe showed after checkout, or contact support.",
      });
      return;
    }

    const controller = new AbortController();
    setHandoff({ status: "loading" });
    void fetch(`/api/purchase-handoff?session_id=${encodeURIComponent(sessionId)}`, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("handoff unavailable");
        const body = (await response.json()) as { oneLinkUrl?: unknown };
        if (typeof body.oneLinkUrl !== "string") throw new Error("handoff unavailable");
        const url = new URL(body.oneLinkUrl);
        if (url.protocol !== "https:" || !url.hostname.endsWith(".onelink.me")) {
          throw new Error("handoff unavailable");
        }
        setHandoff({ status: "ready", oneLinkUrl: url.toString() });
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setHandoff({
          status: "error",
          message: "Your purchase is safe. We couldn’t load the app link yet—please try again.",
        });
      });

    return () => controller.abort();
  }, [attempt]);

  return (
    <main className="not-found shell purchase-welcome">
      <p className="kicker">You’re in</p>
      <h1>Your plan is set up.</h1>
      <p className="lede">
        Your receipt is on its way. Open Vessa from this link so the app can recognise your plan.
      </p>
      {handoff.status === "loading" ? (
        <p className="handoff-status" role="status">
          Preparing your app link…
        </p>
      ) : handoff.status === "ready" ? (
        <div className="handoff-ready">
          <a href={handoff.oneLinkUrl} className="button">
            Open Vessa <Arrow />
          </a>
          <div className="handoff-qr">
            <QRCodeSVG
              value={handoff.oneLinkUrl}
              size={168}
              marginSize={2}
              title="Scan to open Vessa on your iPhone"
            />
            <p>On a computer? Scan with your iPhone camera.</p>
          </div>
        </div>
      ) : (
        <div className="handoff-error" role="alert">
          <p>{handoff.message}</p>
          <button className="button" type="button" onClick={() => setAttempt((value) => value + 1)}>
            Try again
          </button>
        </div>
      )}
      <p className="handoff-help">
        Need help? <Link href="/support">Contact support</Link>
      </p>
    </main>
  );
}

function NotFound() {
  return (
    <main className="not-found shell">
      <p className="kicker">404</p>
      <h1>That page isn’t here.</h1>
      <p className="lede">Try the home page, support, or the official medication sources.</p>
      <Link href="/" className="button">
        Go home <Arrow />
      </Link>
    </main>
  );
}

/** The pages in `public/sitemap.xml`, and the only ones that carry a canonical. */
const indexablePaths = new Set(["/", "/support", "/privacy", "/medication-sources"]);

export function App() {
  const [path, setPath] = useState(window.location.pathname);
  const quizStarted = useRef(false);
  const metaQuizStarted = useRef(false);
  const metaPageViews = useRef(new Set<string>());
  useEffect(() => {
    const update = () => setPath(window.location.pathname);
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);
  useEffect(() => {
    if (path === "/quiz" && !quizStarted.current) {
      quizStarted.current = captureGrowthEvent("quiz_started", takeQuizStartProperties());
    }
    if (path === "/quiz" && !metaQuizStarted.current) {
      metaQuizStarted.current = true;
      void trackMetaEvent("QuizStart", "/quiz");
    }
  }, [path]);
  useEffect(() => {
    if (metaPageViews.current.has(path)) return;
    metaPageViews.current.add(path);
    void trackMetaEvent("PageView", path);
  }, [path]);
  const titles: Record<string, string> = {
    "/": "Vessa — A clearer treatment history",
    "/quiz": "Your Vessa plan",
    "/offer": "Your Vessa plan",
    "/welcome": "You're in — Vessa",
    "/support": "Support — Vessa",
    "/privacy": "Privacy — Vessa",
    "/medication-sources": "Official medication sources — Vessa",
    "/dev": "Funnel board — Vessa",
  };
  useEffect(() => {
    document.title = titles[path] ?? "Page not found — Vessa";
  }, [path]);
  /* index.html ships one canonical for the apex, and the router then moves the
     page under it without a reload. Only the pages in the sitemap get one; the
     funnel paths are not a place search should send anybody, so theirs is
     removed rather than pointed somewhere flattering. */
  useEffect(() => {
    const tag = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!tag) return;
    if (indexablePaths.has(path)) tag.href = `${site.url}${path}`;
    else tag.removeAttribute("href");
  }, [path]);
  if (path === "/quiz")
    return (
      <Suspense fallback={null}>
        <Quiz />
      </Suspense>
    );
  /* The offer on its own, which is where Stripe sends a cancelled checkout. The
     funnel keeps no answers by design, so there is nothing to return anyone to;
     this at least returns them to the thing they were deciding about, minus the
     personalized code there is no longer a name for. */
  if (path === "/offer")
    return (
      <Suspense fallback={null}>
        <CurrentPaywall />
      </Suspense>
    );
  if (path === "/dev" && DevBoard)
    return (
      <Suspense fallback={null}>
        <DevBoard />
      </Suspense>
    );
  /* The board's landing tiles load this in an iframe rather than mounting <Home>
     inside a 390px box. A box is not a viewport: the page's media queries would
     read the desktop window around it and lay a desktop page out at phone width,
     which is a picture of a bug the site does not have. */
  if (path === "/dev/landing" && devBoardEnabled) {
    const asked = new URLSearchParams(window.location.search).get("variant");
    return (
      <Chrome>
        <Home variant={isVariantOf("landing-message", asked) ? asked : undefined} />
      </Chrome>
    );
  }
  const page =
    path === "/" ? (
      <Home />
    ) : path === "/welcome" ? (
      <Welcome />
    ) : path === "/support" ? (
      <Support />
    ) : path === "/privacy" ? (
      <Privacy />
    ) : path === "/medication-sources" ? (
      <MedicationSources />
    ) : (
      <NotFound />
    );
  return <Chrome>{page}</Chrome>;
}

/* The template's page frame: blurred ground, nav and content padded as one
   isolated stack, footer pinned past the fold. The lp-root reset feeds the
   Tailwind chrome; site.css outranks it wherever a legacy page has a say. */
function Chrome({ children }: { children: ReactNode }) {
  return (
    <>
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <div className="lp-root flex h-full min-h-screen w-full flex-col justify-between">
        <div className="isolate flex w-full flex-col p-8">
          <BackgroundBlur />
          <Nav />
          <div id="main-content">{children}</div>
        </div>
        <Footer />
      </div>
    </>
  );
}
